/**
 * Shared Form Styles for Auth Modals
 *
 * Reusable styled components for Login, CreateAccount, and ForgotPassword modals.
 * These components provide consistent spacing, layout, and styling across all auth forms.
 *
 * Usage:
 * import * as FormStyled from './AuthModal.form.style';
 *
 * <FormStyled.FormRoot>
 *   <FormStyled.FormStack>
 *     <FormStyled.TitleContainer>
 *       <FormStyled.Title>Log in</FormStyled.Title>
 *       <FormStyled.Subtitle>...</FormStyled.Subtitle>
 *     </FormStyled.TitleContainer>
 *     ...
 *   </FormStyled.FormStack>
 * </FormStyled.FormRoot>
 */

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

/**
 * FormRoot
 * Wrapper for the entire form element
 */
export const FormRoot = superstyled({
  name: 'AuthModalForm',
  slot: 'FormRoot',
  tag: Box,
  styles: () => ({
    component: 'form',
    noValidate: true
  })
});

/**
 * FormStack
 * Main vertical stack for form content
 * Consistent spacing between form sections
 */
export const FormStack = superstyled({
  name: 'AuthModalForm',
  slot: 'FormStack',
  tag: Stack,
  styles: ({ theme }) => ({
    direction: 'column',
    gap: theme.spacing(2)
  })
});

/**
 * TitleContainer
 * Container for title and subtitle
 * Groups the heading content together
 */
export const TitleContainer = superstyled({
  name: 'AuthModalForm',
  slot: 'TitleContainer',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  })
});

/**
 * Title
 * Main heading for the modal
 * Usage: <FormStyled.Title size="lg" weight="bold">Create your account</FormStyled.Title>
 */
export const Title = superstyled({
  name: 'AuthModalForm',
  slot: 'Title',
  tag: Text,
  styles: () => ({
    // Size and weight passed as props
  })
});

/**
 * Subtitle
 * Secondary text below the title
 * Used for helper text and modal switching links
 */
export const Subtitle = superstyled({
  name: 'AuthModalForm',
  slot: 'Subtitle',
  tag: Text,
  styles: () => ({})
});

/**
 * SwitchLink
 * Link to switch between modals (Login <-> CreateAccount <-> ForgotPassword)
 * Usage: <FormStyled.SwitchLink onClick={handleSwitch}>Log in</FormStyled.SwitchLink>
 */
export const SwitchLink = superstyled({
  name: 'AuthModalForm',
  slot: 'SwitchLink',
  tag: Link,
  styles: ({ theme }) => ({
    component: 'button',
    type: 'button',
    color: theme.palette.info.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  })
});

/**
 * FieldStack
 * Container for form fields
 * Provides consistent spacing between inputs
 */
export const FieldStack = superstyled({
  name: 'AuthModalForm',
  slot: 'FieldStack',
  tag: Stack,
  styles: ({ theme }) => ({
    direction: 'column',
    gap: theme.spacing(1.5)
  })
});

/**
 * ButtonStack
 * Container for action buttons (Submit + Cancel/Close)
 * Stacks buttons vertically with consistent spacing
 */
export const ButtonStack = superstyled({
  name: 'AuthModalForm',
  slot: 'ButtonStack',
  tag: Stack,
  styles: ({ theme, direction }) => ({
    flexDirection: direction || 'row',
    gap: theme.spacing(1),
    '& > *': {
      flex: 1
    }
  })
});

/**
 * SubmitButton
 * Primary action button (Create Account, Log In, Reset Password)
 * Full-width, large, contained variant
 */
export const SubmitButton = superstyled({
  name: 'AuthModalForm',
  slot: 'SubmitButton',
  tag: Button,
  styles: () => ({
    variant: 'contained',
    size: 'large',
    fullWidth: true
  })
});

/**
 * SecondaryButton
 * Secondary action button (Cancel, Close, Back)
 * Full-width, large, outlined variant
 */
export const SecondaryButton = superstyled({
  name: 'AuthModalForm',
  slot: 'SecondaryButton',
  tag: Button,
  styles: () => ({
    variant: 'outlined',
    size: 'large',
    fullWidth: true
  })
});

/**
 * HelperText
 * Helper text for forms (e.g., "Forgot password?" link)
 * Small text with subtle color
 */
export const HelperText = superstyled({
  name: 'AuthModalForm',
  slot: 'HelperText',
  tag: Text,
  styles: () => ({})
});
